import { useMutation } from '@tanstack/react-query';
import { useModal } from 'components/modals';
import { ModalType } from 'components/modals/modalComponents';
import { ModalProps } from 'components/modals/types';
import { useDeleteAccountPreventionModal } from 'components/profile/components/pricing-plan-card/DeleteAccountPreventionModal';
import { useCanDeleteAccount } from 'components/profile/components/pricing-plan-card/useCanDeleteAccount';
import { createProgressToast, ProgressToastFunctions } from 'components/toasts/createProgressToast';
import { toastWidthAutoClassName } from 'components/toasts/StyledToastContainer';
import Toast from 'components/toasts/Toast';
import { NewAppCookiesKey } from 'config/NewAppCookiesKey';
import { getRoutePath, getRouteUrl } from 'config/routes';
import { BrandVoiceDuplicationSuccessToastContent } from 'features/brand-hub-v2/BrandVoiceDuplicationSuccessToastContent';
import { TargetAudienceDuplicationSuccessToastContent } from 'features/brand-hub-v2/TargetAudienceDuplicationSuccessToastContent';
import { useCreateAiWriterProjectMutation } from 'features/brand-hub-v2/useCreateAiWriterProjectMutation';
import { invalidateBrandVoiceInfiniteQuery } from 'features/brand-voice/useGetBrandVoicesInfinite';
import { forcedLogout, updateCurrentUser } from 'features/customer/store/actions';
import { getIsApiCustomer } from 'features/customer/store/selectors';
import { resetCustomerPreferencesQuery } from 'features/customerPreferences/useCustomerPreferences';
import { resetPreferredLocaleQuery } from 'features/customerPreferences/usePreferredLocaleQuery';
import { useReferralLinkButtonBehavior } from 'features/inviteFriends/referralLink';
import { setLanguage } from 'features/language/store/reducer';
import { AppLanguage } from 'features/language/store/types';
import { useSidebarStore } from 'features/pageSidebar/useSidebarStore';
import { createStripeCheckoutUrlFromResponse } from 'features/stripe-portal/createStripeCheckoutUrlFromResponse';
import { invalidateTargetAudiencesInfiniteQuery } from 'features/target-audience/useGetTargetAudiencesInfinite';
import { invalidateWorkspaceInfoQuery } from 'features/workspaces/useGetWorkspaceInfo';
import Cookies from 'js-cookie';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import CustomerAPI from 'services/api/customer';
import { CreateBillingPortalResponse } from 'services/api/customer/types';
import { useRequestIdHeaderStore } from 'services/backofficeIntegration/http/client/useRequestIdHeaderStore';
import { GAEvents } from 'services/tracking/GAEvents';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { dispatchWithLazyRootStore } from 'store/lazyRootStoreAccessor';
import { ThemeMode, useAppThemeStore } from 'styles/new-theme/useAppThemeStore';
import { changeUrlWithoutReload } from 'utils/changeUrlWithoutReload';
import useTr from 'utils/hooks/useTr';
import redirectToStripe from 'utils/redirectToStripe';

type ReceivedNavigationMessage = {
  type: 'nav';
  path: string;
};

type ReceivedWindowSizeMessage = {
  type: 'size';
  width: number;
  height: number;
};

type ReceivedModalMessage = {
  type: 'show-modal';
  modalId: ModalType;
  options?: ModalProps;
};

type ReceivedPathChangeMessage = {
  type: 'path-change';
  path: string;
};

type ReceivedReloadPageMessage = {
  type: 'reload-page';
  path: string;
};

type ReceivedRequestIdHeaderMessage = {
  type: 'set-request-id';
  requestId: string;
};

type ReceivedBrandVoiceCreatedMessage = {
  type: 'brand-voice-created';
};

type ReceivedBrandVoiceDeletedMessage = {
  type: 'brand-voice-deleted';
};

type ReceivedTargetAudienceCreatedMessage = {
  type: 'target-audience-created';
};

type ReceivedTargetAudienceDeletedMessage = {
  type: 'target-audience-deleted';
};

type ReceivedToastErrorCommonMessage = {
  type: 'show-simple-toast-message';
  toastType: 'error' | 'success' | 'info' | 'warning';
  messageId?: string;
  messageValues?: Record<string, string | number>;
};

type ReceivedCollapseSidebarMessage = {
  type: 'collapse-sidebar';
};

type ReceivedCreateNavigateToAiWriterProjectMessage = {
  type: 'create-navigate-to-ai-writer-project';
  config: {
    brandVoiceId: string;
    embeddingModelId: string;
  };
};

type ReceivedShowBrandVoiceDuplicationStartMessage = {
  type: 'brand-voice-duplication-start';
  brandVoiceName: string;
};

type ReceivedShowBrandVoiceDuplicationSuccessMessage = {
  type: 'brand-voice-duplication-success';
  brandVoiceId: string;
  brandVoiceName: string;
};

type ReceivedShowBrandVoiceDuplicationErrorMessage = {
  type: 'brand-voice-duplication-error';
  brandVoiceName: string;
};

type ReceivedChangeAppLanguageMessage = {
  type: 'change-app-language';
  language: AppLanguage;
};

type ReceivedChangePreferredContentLanguageMessage = {
  type: 'change-preferred-content-language';
};

type ReceivedChangeThemeModeMessage = {
  type: 'change-theme-mode';
  themeMode: ThemeMode;
};

type ReceivedGenerateReferralLinkMessage = {
  type: 'generate-referral-link';
};

type ReceivedHideModalMessage = {
  type: 'hide-modal';
};

type ReceivedChangeCustomerNameMessage = {
  type: 'change-customer-profile';
  name?: string;
  image?: string;
};

type ReceivedOpenStripePortalMessage = {
  type: 'open-stripe-portal';
  payload: CreateBillingPortalResponse;
  isActivePlan: boolean;
  hasSubscriptionInGrace: boolean;
};

type ReceivedSendGAEventMessage = {
  type: 'send-faq-ga-event';
  body: { questionId: string };
};

type ReceivedShowTargetAudienceDuplicationStartMessage = {
  type: 'target-audience-duplication-start';
  targetAudienceName: string;
};

type ReceivedShowTargetAudienceDuplicationSuccessMessage = {
  type: 'target-audience-duplication-success';
  targetAudienceId: string;
  targetAudienceName: string;
};

type ReceivedShowTargetAudienceDuplicationErrorMessage = {
  type: 'target-audience-duplication-error';
  targetAudienceName: string;
};

type ReceivedChangeWorkspaceSettingsMessage = {
  type: 'change-workspace-settings';
};

type ReceivedLogoutMessage = {
  type: 'logout';
};

type ReceivedRequestAccountDeletionMessage = {
  type: 'request-account-deletion';
};

type ReceivedIframeMessage =
  | ReceivedNavigationMessage
  | ReceivedWindowSizeMessage
  | ReceivedPathChangeMessage
  | ReceivedReloadPageMessage
  | ReceivedModalMessage
  | ReceivedRequestIdHeaderMessage
  | ReceivedToastErrorCommonMessage
  | ReceivedBrandVoiceCreatedMessage
  | ReceivedBrandVoiceDeletedMessage
  | ReceivedCollapseSidebarMessage
  | ReceivedCreateNavigateToAiWriterProjectMessage
  | ReceivedShowBrandVoiceDuplicationStartMessage
  | ReceivedShowBrandVoiceDuplicationSuccessMessage
  | ReceivedShowBrandVoiceDuplicationErrorMessage
  | ReceivedChangeAppLanguageMessage
  | ReceivedChangePreferredContentLanguageMessage
  | ReceivedChangeThemeModeMessage
  | ReceivedGenerateReferralLinkMessage
  | ReceivedChangeCustomerNameMessage
  | ReceivedHideModalMessage
  | ReceivedOpenStripePortalMessage
  | ReceivedSendGAEventMessage
  | ReceivedTargetAudienceCreatedMessage
  | ReceivedTargetAudienceDeletedMessage
  | ReceivedShowTargetAudienceDuplicationStartMessage
  | ReceivedShowTargetAudienceDuplicationSuccessMessage
  | ReceivedShowTargetAudienceDuplicationErrorMessage
  | ReceivedChangeWorkspaceSettingsMessage
  | ReceivedLogoutMessage
  | ReceivedRequestAccountDeletionMessage;

const useRequestAccountDeletion = () => {
  const { showModal } = useModal();
  const showDeleteAccountPreventionModal = useDeleteAccountPreventionModal();
  const { canDeleteAccount } = useCanDeleteAccount();

  return () => {
    if (!canDeleteAccount) {
      showDeleteAccountPreventionModal();
      return;
    }

    showModal('ACCOUNT_DELETE', { size: 900 });
  };
};

export function useIframeMessageListener() {
  const translate = useTr();
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const { onClick: generateLink } = useReferralLinkButtonBehavior();

  const dispatch = useAppDispatch();
  const isApiCustomer = useAppSelector(getIsApiCustomer);
  const workspaceId = useAppSelector(state => state.workspace.workspaceId);

  const setThemeMode = useAppThemeStore(state => state.setThemeMode);

  const { mutateAsync: updateCustomer } = useMutation(
    async ({ locale, name }: { locale?: AppLanguage; name?: string }) => {
      await CustomerAPI.update({ payload: { locale, name } });
    }
  );

  const duplicationToastRef = useRef<ProgressToastFunctions | null>(null);

  const setRequestId = useRequestIdHeaderStore(state => state.setRequestIdHeader);
  const collapseSidebar = useSidebarStore(state => state.collapseSidebar);

  const brandHubBasePath = getRouteUrl('brandHubV2');

  const { mutateAsync: createAiWriterProject } = useCreateAiWriterProjectMutation();

  const requestAccountDeletion = useRequestAccountDeletion();

  useEffect(() => {
    const onEventMessage = async (evt: MessageEvent) => {
      // TODO not working on local
      // if (evt.origin !== brandHubIframeUrl) {
      //   return;
      // }

      const data = evt.data as ReceivedIframeMessage;

      if (data.type === 'nav') {
        navigate(data.path);
      }

      if (data.type === 'path-change') {
        // This changes the URL in the browser, but does not reload the page
        changeUrlWithoutReload(data.path);
      }

      if (data.type === 'reload-page') {
        window.location.reload();
      }

      if (data.type === 'show-modal') {
        showModal(data.modalId, data?.options);
      }

      if (data.type === 'hide-modal') {
        hideModal();
      }

      if (data.type === 'size') {
        // if needed, set the size of the iframe
      }

      if (data.type === 'set-request-id') {
        setRequestId(data.requestId);
      }

      if (data.type === 'show-simple-toast-message') {
        if (data.messageId) {
          Toast[data.toastType](data.messageId, data.messageValues);
        } else {
          Toast.commonError();
        }
      }

      if (data.type === 'brand-voice-created' || data.type === 'brand-voice-deleted') {
        // Invalidate queries to re-trigger user data dataLayer update
        invalidateBrandVoiceInfiniteQuery();
      }

      if (data.type === 'target-audience-created' || data.type === 'target-audience-deleted') {
        // Invalidate queries to re-trigger user data dataLayer update
        invalidateTargetAudiencesInfiniteQuery();
      }

      if (data.type === 'collapse-sidebar') {
        collapseSidebar();
      }

      if (data.type === 'create-navigate-to-ai-writer-project') {
        await createAiWriterProject(data.config);

        navigate(getRoutePath('aiWriter'));
      }

      if (data.type === 'brand-voice-duplication-start') {
        duplicationToastRef.current?.close();
        duplicationToastRef.current = createProgressToast(
          translate('brand_hub.brand_voice.duplication_toast.title', {
            name: data.brandVoiceName,
          })
        );
      }

      if (data.type === 'brand-voice-duplication-success') {
        duplicationToastRef.current?.success({
          render: (
            <BrandVoiceDuplicationSuccessToastContent
              brandVoiceId={data.brandVoiceId}
              brandVoiceName={data.brandVoiceName}
              toast={duplicationToastRef.current}
            />
          ),
          autoClose: Toast.defaultAutoClose,
          bodyClassName: toastWidthAutoClassName,
        });
      }

      if (data.type === 'brand-voice-duplication-error') {
        duplicationToastRef.current?.error({
          render: translate('brand_hub.brand_voice.duplication.error', {
            name: data.brandVoiceName,
          }),
          autoClose: Toast.defaultAutoClose,
        });
      }

      if (data.type === 'target-audience-duplication-start') {
        duplicationToastRef.current?.close();
        duplicationToastRef.current = createProgressToast(
          translate('brand_hub.target_audience.duplication_toast.title', {
            name: data.targetAudienceName,
          })
        );
      }

      if (data.type === 'target-audience-duplication-success') {
        duplicationToastRef.current?.success({
          render: (
            <TargetAudienceDuplicationSuccessToastContent
              targetAudienceName={data.targetAudienceName}
              targetAudienceId={data.targetAudienceId}
              toast={duplicationToastRef.current}
            />
          ),
          autoClose: Toast.defaultAutoClose,
          bodyClassName: toastWidthAutoClassName,
        });
      }

      if (data.type === 'target-audience-duplication-error') {
        duplicationToastRef.current?.error({
          render: translate('brand_hub.target_audience.duplication.error', {
            name: data.targetAudienceName,
          }),
          autoClose: Toast.defaultAutoClose,
        });
      }

      if (data.type === 'change-app-language') {
        if (!isApiCustomer) {
          await updateCustomer({ locale: data.language });
        }

        dispatch(setLanguage(data.language));
        Cookies.set(NewAppCookiesKey.appLanguage, data.language);
      }

      if (data.type === 'change-preferred-content-language') {
        resetCustomerPreferencesQuery();
        resetPreferredLocaleQuery();
      }

      if (data.type === 'change-theme-mode') {
        setThemeMode(data.themeMode);

        // Update body class name
        if (data.themeMode === 'dark') {
          document.body.classList.add('dark');
        } else {
          document.body.classList.remove('dark');
        }

        if (data.themeMode === 'dark') {
          GAEvents.darkModeEnabled();
        } else {
          GAEvents.darkModeDisabled();
        }
      }

      if (data.type === 'generate-referral-link') {
        generateLink();
      }

      if (data.type === 'change-customer-profile') {
        dispatch(
          updateCurrentUser.request({ name: data.name, image: data.image }, { thunk: true })
        );
      }

      if (data.type === 'open-stripe-portal') {
        const redirectUrl = createStripeCheckoutUrlFromResponse(
          data.payload,
          data.isActivePlan,
          data.hasSubscriptionInGrace
        );

        redirectToStripe(redirectUrl);
      }

      if (data.type === 'send-faq-ga-event') {
        GAEvents.FAQquestionClicked(data.body);
      }

      if (data.type === 'change-workspace-settings') {
        invalidateWorkspaceInfoQuery(workspaceId);
      }

      if (data.type === 'logout') {
        dispatchWithLazyRootStore(forcedLogout());
      }

      if (data.type === 'request-account-deletion') {
        requestAccountDeletion();
      }
    };

    window.addEventListener('message', onEventMessage);

    const duplicationToast = duplicationToastRef.current;
    return () => {
      window.removeEventListener('message', onEventMessage);

      // Cleanup duplication toast ref
      duplicationToast?.close();
      duplicationToastRef.current = null;
    };
  }, [
    brandHubBasePath,
    collapseSidebar,
    createAiWriterProject,
    navigate,
    setRequestId,
    showModal,
    translate,
    dispatch,
    generateLink,
    isApiCustomer,
    setThemeMode,
    updateCustomer,
    hideModal,
    workspaceId,
    requestAccountDeletion,
  ]);
}
